import { Injectable } from '@angular/core';
import { OrganizationStatus } from '@processpuzzle/platform-admin';
import { AdministratorAccount, BillingParty, PostalAddress, SeedState, Signup, SignupDraft, SignupRequest, SupportedLocale } from './signup';

type SignupDto = Partial<Signup>;

/**
 * Between the wire and this library's models.
 *
 * Both directions matter here, which is unusual for this repository: the funnel is the one surface
 * in the platform that writes a whole aggregate, so {@link SignupMapper.toRequest} is as
 * load-bearing as the read. What it mostly does is turn a {@link SignupDraft} — every field
 * optional, because it is being filled in one step at a time — into the complete
 * {@link SignupRequest} the contract requires.
 *
 * There is no `toDto` for {@link Signup}: nothing writes one, because its state is a fact about
 * provisioning rather than something a client sets.
 */
@Injectable({ providedIn: 'root' })
export class SignupMapper {
  fromDto(dto: unknown): Signup {
    const source = dto as SignupDto;
    return {
      orgKey: source.orgKey ?? '',
      status: (source.status as OrganizationStatus | undefined) ?? OrganizationStatus.PROVISIONING,
      // Present only once ACTIVE, as the contract specifies. Read as it arrives rather than
      // reconstructed from the key: the hostname template is the server's to decide.
      applicationUrl: source.applicationUrl,
      trialEndsAt: source.trialEndsAt,
      // All three absent for any tenant with no seed job, which is every tenant not created through
      // this funnel. Absent stays absent rather than becoming PENDING: "no job" and "a job that has
      // not started" are different states, and only the second is worth waiting on.
      seedState: source.seedState as SeedState | undefined,
      failedStep: source.failedStep as SeedState | undefined,
      seedError: source.seedError,
      administratorAccount: source.administratorAccount as AdministratorAccount | undefined,
    };
  }

  /**
   * The draft as the contract's payload.
   *
   * Every `??` here is reached only by a draft that failed `isDraftComplete`, which the store checks
   * before submitting — they are present so that this method has no partial types in its signature,
   * not as defaults anybody should rely on. The one meaningful fallback is the administrator's
   * locale, which the contract itself specifies as inherited from the organization.
   *
   * Strings are trimmed on the way out. A legal name or a VAT ID with a trailing space is the same
   * company with a different database row, and the paste that produced it is not something to make
   * the customer notice.
   */
  toRequest(draft: SignupDraft): SignupRequest {
    const organization = draft.organization ?? {};
    const administrator = draft.administrator ?? {};
    const subscription = draft.subscription ?? {};
    const consent = draft.consent ?? {};
    const locale = organization.defaultLocale ?? SupportedLocale.EN;

    return {
      organization: {
        key: (organization.key ?? '').trim(),
        name: (organization.name ?? '').trim(),
        defaultLocale: locale,
      },
      billingParty: this.toBillingParty(draft.billingParty ?? {}),
      administrator: {
        email: (administrator.email ?? '').trim(),
        firstName: (administrator.firstName ?? '').trim(),
        lastName: (administrator.lastName ?? '').trim(),
        // Empty becomes absent rather than an empty string: the identity provider stores what it is
        // given, and an empty phone number is a value rather than the absence of one.
        phone: (administrator.phone ?? '').trim() || undefined,
        defaultLocale: administrator.defaultLocale ?? locale,
      },
      subscription: {
        planCode: subscription.planCode ?? '',
        quantity: subscription.quantity ?? 1,
        // Empty is "an empty application", which the contract expresses as absent.
        starterId: subscription.starterId || undefined,
      },
      consent: {
        termsOfServiceVersion: consent.termsOfServiceVersion ?? '',
        dataProcessingAgreementVersion: consent.dataProcessingAgreementVersion ?? '',
        marketingOptIn: consent.marketingOptIn ?? false,
      },
    };
  }

  /**
   * The invoiced party, shared by the funnel and the portal — one form, one mapping.
   *
   * The billing address is sent only when it is actually filled in. An absent one means "invoices go
   * to the registered address", which is a different statement from a copy of it: a copy would stop
   * following the registered address the moment it changed.
   */
  toBillingParty(draft: Partial<BillingParty>): BillingParty {
    return {
      legalName: (draft.legalName ?? '').trim(),
      legalForm: (draft.legalForm ?? '').trim(),
      customerType: draft.customerType as BillingParty['customerType'],
      registeredAddress: this.toAddress(draft.registeredAddress),
      billingAddress: this.hasAddress(draft.billingAddress) ? this.toAddress(draft.billingAddress) : undefined,
      vatId: (draft.vatId ?? '').trim() || undefined,
      registrationNumber: (draft.registrationNumber ?? '').trim() || undefined,
      billingEmail: (draft.billingEmail ?? '').trim(),
    };
  }

  private toAddress(address: Partial<PostalAddress> | undefined): PostalAddress {
    return {
      street: (address?.street ?? '').trim(),
      postalCode: (address?.postalCode ?? '').trim(),
      city: (address?.city ?? '').trim(),
      // Upper case because the contract's pattern is `^[A-Z]{2}$` and a select can be repopulated
      // from a draft that a browser autofilled in lower case.
      country: (address?.country ?? '').trim().toUpperCase(),
    };
  }

  /** Any filled field counts: a partly filled billing address is an error to show, not one to drop. */
  private hasAddress(address: Partial<PostalAddress> | undefined): boolean {
    if (!address) return false;
    return [address.street, address.postalCode, address.city, address.country].some((value) => !!value && value.trim().length > 0);
  }
}
